import { useSyncExternalStore } from 'react'

/**
 * The brand-scoped app shell, for code that cannot reach the React context.
 *
 * Plain functions in a plain module, deliberately: `portal-container.tsx`
 * exports components, and adding non-component exports there trips
 * `react-refresh/only-export-components` and costs Fast Refresh on every
 * overlay in the app. Same mechanism as that file, same node, one owner.
 *
 * `InstallPrompt` and `UpdateBanner` live in `App.tsx`, as siblings of
 * `RouterProvider`, because they must float over public routes too. That puts
 * them outside `AppLayout`'s shell `<div data-brand-scope>`, so every tenant
 * custom property they ask for resolves to EventRipple's default instead.
 *
 * Measured 13.10.2026, signed in as a Nortura admin on `/dashboard`: the install
 * card drew Nortura's icon and the name «PROFF Event» while
 * `--action-primary-bg` on the shell beside it was #111111 and the card painted
 * #4f46e5. Our indigo, on a card wearing somebody else's mark. It was latent in
 * August and went live when `useTenantAppIdentity` started publishing the mark
 * on every signed-in route, which removed the suppression rule that had been
 * hiding the card on exactly those pages.
 *
 * A context cannot carry it: the reader sits above the provider, and wrapping
 * the whole app in one more provider to move a single DOM node is the wrong
 * trade. A `querySelector('[data-brand-scope]')` cannot either — `BrandPreview`
 * publishes a SECOND scope inside Branding settings, and the floating card would
 * happily mount into the admin's little preview card and wear whatever colour
 * they were trying out. So the owner states it explicitly: `AppLayout` already
 * holds the node, and publishes that exact one here.
 *
 * `null` means «no brand-scoped shell is mounted» (public and auth layouts),
 * where the vars sit on `documentElement` and ordinary inheritance already
 * works — floating chrome then stays where it is.
 */
let brandShellNode: HTMLElement | null = null
const brandShellListeners = new Set<() => void>()

/** AppLayout only. Publishes the shell that carries the tenant palette. */
export function setBrandShellNode(node: HTMLElement | null): void {
  if (brandShellNode === node) return
  brandShellNode = node
  for (const listener of brandShellListeners) listener()
}

export function getBrandShellNode(): HTMLElement | null {
  return brandShellNode
}

export function subscribeBrandShellNode(listener: () => void): () => void {
  brandShellListeners.add(listener)
  return () => {
    brandShellListeners.delete(listener)
  }
}

/**
 * Where floating chrome should render so it inherits the tenant palette: the
 * brand-scoped shell when one is mounted, otherwise `null` for «stay put».
 *
 * `useSyncExternalStore` rather than an effect, so the first paint already has
 * the right parent instead of mounting into the wrong palette and moving.
 */
export function useBrandShellNode(): HTMLElement | null {
  return useSyncExternalStore(subscribeBrandShellNode, getBrandShellNode, () => null)
}
